import { FunctionComponent, memo, useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { IVideoTutorialCategory } from "./IVideoTutorialCategory";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import TextInputFieldC from "../../Components/TextInput/TextInputFieldC";
import { useAppDispatch } from "../../Redux/Store/Hooks";
import { addVideoTutorialCategory } from "../../Redux/Slices/VideoTutorialCategory/AddVideoTutorialCategorySlice";
import { editVideoTutorialCategory } from "../../Redux/Slices/VideoTutorialCategory/EditVideoTutorialCategorySlice";
import Select from "../../Components/Select/AutoComplete";
import { AutocompleteOption } from "../../Components/Select/ISelectProps";

interface VideoTutorialCategoryFormProps {
    formRef: React.RefObject<HTMLFormElement>;
    initialValues: Partial<IVideoTutorialCategory>;
    setInitialValues: React.Dispatch<React.SetStateAction<Partial<IVideoTutorialCategory>>>;
}

const VideoTutorialCategoryForm: FunctionComponent<VideoTutorialCategoryFormProps> = (props) => {
    const dispatch = useAppDispatch();

    const categorySchema = z.object({
        id: z.number().optional(),
        meta_value: z.string({
            required_error: "Please enter category name",
            invalid_type_error: "Please enter category name",
        }).min(1, "Please enter category name"),
        meta_key: z.string().optional(),
        description: z.string().optional(),
        status: z.boolean(),
    });

    const methods = useForm<IVideoTutorialCategory>({
        defaultValues: {
            ...props.initialValues as IVideoTutorialCategory,
            status: props.initialValues.status ?? true,
        },
        resolver: zodResolver(categorySchema),
        mode: "onChange",
    });

    const onSubmit: SubmitHandler<IVideoTutorialCategory> = (val) => {
        if (val.id) {
            dispatch(editVideoTutorialCategory(val));
        } else {
            dispatch(addVideoTutorialCategory({ ...val, id: 0, meta_key: "video_category" }));
        }
    };

    const statusOptions = [
        { label: "Active", value: true },
        { label: "Inactive", value: false },
    ];

    useEffect(() => {
        return () => {
            props.setInitialValues({});
        };
    }, []);

    return (
        <FormProvider {...methods}>
            <form
                ref={props.formRef}
                onSubmit={methods.handleSubmit(onSubmit)}
                className="form_content"
            >
                <TextInputFieldC name="meta_value" label="Category Name" type="text" />
                <Select
                    name="status"
                    label="Status"
                    options={statusOptions}
                    value={statusOptions.find(opt => opt.value === methods.getValues("status")) ?? null}
                    onChange={(_, value) => {
                        const val = value as AutocompleteOption;
                        methods.setValue("status", val?.value as boolean, { shouldValidate: true });
                    }}
                />
            </form>
        </FormProvider>
    );
};

export default memo(VideoTutorialCategoryForm);
